import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsers } from "../../../../store/Users/usersThunks";
import { createPlaylist } from "../../../../store/Playlists/playlistsThunks";

import InfoStep from "./InfoStep";
import ShareStep from "./ShareStep";
import SummaryStep from "./SummaryStep";

function CompetencyPlaylistForm({ onSubmit, row }) {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const reduxUsers = useSelector((state) => state.users.users);
  const usersLoading = useSelector((state) => state.users.loading);

  const [activeStep, setActiveStep] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    startDateTime: "",
    endDateTime: "",
    isPublic: false,
    selectedUsers: [],
  });

  useEffect(() => {
    if (currentUser?.id && formData.selectedUsers.length === 0) {
      setFormData((prev) => ({
        ...prev,
        selectedUsers: [currentUser.id],
      }));
    }
  }, [currentUser]);

  const steps = ["Info", "Share", "Summary"];

  useEffect(() => {
    if (activeStep === 1) {
      dispatch(fetchUsers({}));
    }
  }, [activeStep, dispatch]);

  const validateStep = () => {
    const errors = {};
    const {
      name,
      description,
      startDateTime,
      endDateTime,
      isPublic,
      selectedUsers,
      ppsNumber,
      worksite,
    } = formData;

    if (activeStep === 0) {
      if (!name) errors.name = "Name is required";
      if (!description) errors.description = "Description is required";
      if (!startDateTime) errors.startDateTime = "Start date is required";
      if (!endDateTime) errors.endDateTime = "End date is required";
      if (!ppsNumber && !worksite) {
        errors.ppsNumber = "Either PPS Number or Worksite is required";
        errors.worksite = "Either PPS Number or Worksite is required";
      }
    }

    if (activeStep === 1) {
      if (!isPublic && (!selectedUsers || selectedUsers.length === 0)) {
        errors.selectedUsers = "Please select at least one user to share with.";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const toggleUserSelection = (userId) => {
    setFormData((prev) => {
      const selectedUsers = prev.selectedUsers || [];
      return selectedUsers.includes(userId)
        ? {
            ...prev,
            selectedUsers: selectedUsers.filter((id) => id !== userId),
          }
        : { ...prev, selectedUsers: [...selectedUsers, userId] };
    });
  };

  const toggleSelectAll = () => {
    setFormData((prev) => ({
      ...prev,
      selectedUsers:
        prev.selectedUsers.length === reduxUsers.length
          ? []
          : reduxUsers.map((u) => u.id),
    }));
  };

  const handleFinalSubmit = async () => {
    if (!validateStep()) return;

    const finalData = {
      playlistName: formData.name,
      playlistDescription: formData.description,
      isPublic: formData.isPublic,
      sharedUserIds: formData.isPublic ? [] : formData.selectedUsers,
      playlistType: "competency",
      ppsNumber: formData.ppsNumber,
      worksite: formData.worksite,
      recordedCallFilters: [
        {
          field: "owner_recorded_contact_id",
          operator: "equals",
          value: row.id,
        },
        {
          field: "start_time",
          operator: "greaterThanOrEqual",
          value: formData.startDateTime,
        },
        {
          field: "end_time",
          operator: "lessThanOrEqual",
          value: formData.endDateTime,
        },
      ],
    };

    const result = await dispatch(createPlaylist(finalData));
    if (result.meta.requestStatus === "fulfilled") {
      onSubmit?.(result.finalData);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <InfoStep
            formData={formData}
            formErrors={formErrors}
            handleChange={handleChange}
          />
        );
      case 1:
        return (
          <ShareStep
            formData={formData}
            setFormData={setFormData}
            reduxUsers={reduxUsers}
            usersLoading={usersLoading}
            formErrors={formErrors}
            toggleUserSelection={toggleUserSelection}
            toggleSelectAll={toggleSelectAll}
            currentUser={currentUser}
          />
        );
      case 2:
        return (
          <SummaryStep formData={formData} reduxUsers={reduxUsers} row={row} />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {renderStepContent()}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 4 }}>
        {activeStep > 0 && (
          <Button variant="outlined" onClick={handleBack} fullWidth>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext} fullWidth>
            Next
          </Button>
        ) : (
          <Button variant="contained" onClick={handleFinalSubmit} fullWidth>
            Submit
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default CompetencyPlaylistForm;
